/**
 * Localization strings used in the Tree Viewer
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const treeText = createDictionary({
  trees: {
    'en-us': 'Trees',
    'ru-ru': 'Деревья',
    'es-es': 'Árboles',
    'fr-fr': 'Arbres',
    'uk-ua': 'дерева',
    'de-ch': 'Hierarchien',
    'pt-br': 'Árvores',
    'hr-hr': 'Drveće',
    nb: 'Trær',
  },
  badStructure: {
    'en-us': 'Bad tree structure.',
    'ru-ru': 'Неправильная структура дерева.',
    'es-es': 'Estructura de árbol incorrecta.',
    'fr-fr': 'Mauvaise arborescence.',
    'uk-ua': 'Погана структура дерева.',
    'de-ch': 'Fehlerhafte Baumstruktur.',
    'pt-br': 'Má estrutura da árvore.',
    'hr-hr': 'Loša struktura stabla.',
    nb: 'Dårlig trestruktur.',
  },
  move: {
    'en-us': 'Move',
    'ru-ru': 'Двигаться',
    'es-es': 'Mover',
    'fr-fr': 'Déplacer',
    'uk-ua': 'рухатися',
    'de-ch': 'Verschieben',
    'pt-br': 'Mover',
    'hr-hr': 'Potez',
    nb: 'Flytte',
  },
  merge: {
    'en-us': 'Merge',
    'ru-ru': 'Слияние',
    'es-es': 'Unir',
    'fr-fr': 'Fusionner',
    'uk-ua': 'Об’єднати',
    'de-ch': 'Zusammenführen',
    'pt-br': 'Fusão',
    'hr-hr': 'Spojiti',
    nb: 'Slå sammen',
  },
  undoSynonymy: {
    'en-us': 'Undo Synonymy',
    'ru-ru': 'Отменить синонимию',
    'es-es': 'Deshacer sinonimia',
    'fr-fr': 'Annuler la synonymie',
    'uk-ua': 'Скасувати синонімію',
    'de-ch': 'Synonymie rückgängig machen',
    'pt-br': 'Desfazer Sinônimo',
    'hr-hr': 'Poništi sinonimiju',
    nb: 'Angre synonym',
  },
  synonymize: {
    'en-us': 'Synonymize',
    'ru-ru': 'Подбирать синонимы',
    'es-es': 'Sinonimizar',
    'fr-fr': 'Synonymiser',
    'uk-ua': 'Синонімізувати',
    'de-ch': 'Synonymisieren',
    'pt-br': 'Sinônimos',
    'hr-hr': 'Sinonimiziraj',
    nb: 'Synonymisere',
  },
  actionFailed: {
    'en-us': 'Operation failed',
    'ru-ru': 'Операция провалилась',
    'es-es': 'Operación fallida',
    'fr-fr': "L'opération a échoué",
    'uk-ua': 'Операція не вдалася',
    'de-ch': 'Vorgang fehlgeschlagen',
    'pt-br': 'Operação falhou',
    'hr-hr': 'Operacija nije uspjela',
    nb: 'Operasjonen mislyktes',
  },
  actionFailedDescription: {
    'en-us':
      'The operation could not be completed due to the following errors:',
    'ru-ru': 'Операция не может быть завершена из-за следующих ошибок:',
    'es-es':
      'La operación no se pudo completar debido a los siguientes errores:',
    'fr-fr':
      "L'opération n'a pas pu être terminée en raison des erreurs suivantes :",
    'de-ch':
      'Der Vorgang konnte aufgrund der folgenden Fehler nicht ausgeführt werden:',
    'uk-ua': 'Операцію не вдалося завершити через такі помилки:',
    'pt-br': 'A operação não pôde ser concluída devido aos seguintes erros:',
    'hr-hr': 'Operacija nije mogla biti dovršena zbog sljedećih pogrešaka:',
    nb: 'Operasjonen kunne ikke fullføres på grunn av følgende feil:',
  },
  moveNode: {
    'en-us': 'Move node',
    'ru-ru': 'Переместить узел',
    'es-es': 'Mover nodo',
    'fr-fr': 'Déplacer le nœud',
    'uk-ua': 'Перемістити вузол',
    'de-ch': 'Knoten verschieben',
    'pt-br': 'Mover nó',
    'hr-hr': 'Premjesti čvor',
    nb: 'Flytt node',
  },
  addChild: {
    'en-us': 'Add Child',
    'ru-ru': 'Добавить ребенка',
    'es-es': 'Agregar hijo',
    'fr-fr': 'Ajouter un enfant',
    'uk-ua': 'Додати дитину',
    'de-ch': 'Kind hinzuzufügen',
    'pt-br': 'Adicionar filho',
    'hr-hr': 'Dodaj dijete',
    nb: 'Legg til undernode',
  },
  moveNodeHere: {
    'en-us': 'Move "{nodeName:string}" here',
    'ru-ru': 'Переместите "{nodeName:string}" сюда',
    'es-es': 'Mover "{nodeName:string}" aquí',
    'fr-fr': 'Déplacer « {nodeName:string} » ici',
    'uk-ua': 'Перемістіть сюди "{nodeName:string}"',
    'de-ch': 'Verschiebe "{nodeName:string}" hierhin',
    'pt-br': 'Mova "{nodeName:string}" para cá',
    'hr-hr': 'Premjesti "{nodeName:string}" ovdje',
    nb: 'Flytt «{nodeName:string}» hit',
  },
  moveNodePreparationsHere: {
    'en-us': 'Move all "{nodeName:string}" preparations here',
    'de-ch':
      'Verschieben Sie alle Vorbereitungen mit dem Präfix "{nodeName:string}" hierher.',
    'es-es': 'Mover todas las preparaciones "{nodeName:string}" aquí',
    'fr-fr': 'Déplacer toutes les préparations « {nodeName:string} » ici',
    'ru-ru': 'Переместите сюда все приготовления "{nodeName:string}".',
    'uk-ua': 'Перемістіть сюди всі препарати "{nodeName:string}"',
    'pt-br': 'Mova todos os preparativos "{nodeName:string}" para cá.',
    'hr-hr': 'Premjestite sve pripreme "{nodeName:string}" ovdje',
    nb: 'Flytt alle "{nodeName:string}"-forberedelsene hit',
  },
  nodeMoveMessage: {
    'en-us':
      'The {treeName:string} node "{nodeName:string}" will be placed, along with all of its descendants, under the new parent "{parentName:string}".',
    'ru-ru':
      'Узел {treeName:string} "{nodeName:string}" будет помещен вместе со всеми его потомками под новый родительский узел "{parentName:string}".',
    'es-es':
      'El nodo {treeName:string} "{nodeName:string}" se colocará, junto con todos sus descendientes, bajo el nuevo padre "{parentName:string}".',
    'fr-fr':
      'Le nœud {treeName:string} « {nodeName:string} » sera placé, avec tous ses descendants, sous le nouveau parent « {parentName:string} ».',
    'uk-ua':
      'Вузол {treeName:string} "{nodeName:string}" буде розміщено разом із усіма його нащадками під новим батьківським вузлом "{parentName:string}".',
    'de-ch':
      'Der {treeName:string} Knoten "{nodeName:string}" wird zusammen mit allen seinen Unterknoten unter den neuen übergeordneten Knoten "{parentName:string}" platziert.',
    'pt-br':
      'O nó {treeName:string} "{nodeName:string}" será colocado, juntamente com todos os seus descendentes, sob o novo pai "{parentName:string}".',
    'hr-hr':
      'Čvor {treeName:string} "{nodeName:string}" bit će smješten, zajedno sa svim svojim potomcima, pod novim roditeljem "{parentName:string}".',
    nb: '{treeName:string}-noden «{nodeName:string}» vil bli plassert, sammen med alle dens etterkommere, under den nye overordnede noden «{parentName:string}».',
  },
  nodeBulkMoveMessage: {
    'en-us':
      'The {treeName:string} node "{nodeName:string}" preparations will be placed under the new location "{parentName:string}".',
    'de-ch':
      'Die Vorbereitungen für den Knoten {treeName:string} "{nodeName:string}" werden unter dem neuen Standort "{parentName:string}" zusammengefasst.',
    'es-es':
      'Las preparaciones del nodo {treeName:string} "{nodeName:string}" se colocarán en la nueva ubicación "{parentName:string}".',
    'fr-fr':
      'Les préparations du nœud {treeName:string} « {nodeName:string} » seront placés sous le nouvel emplacement « {parentName:string} ».',
    'ru-ru':
      'Подготовка узла {treeName:string} "{nodeName:string}" будет размещена в новом месте "{parentName:string}".',
    'uk-ua':
      'Підготовка вузла {treeName:string} "{nodeName:string}" буде розміщена в новому місці розташування "{parentName:string}".',
    'pt-br':
      'Os preparativos do nó {treeName:string} "{nodeName:string}" serão colocados no novo local "{parentName:string}".',
    'hr-hr':
      'Pripreme čvora {treeName:string} "{nodeName:string}" bit će smještene pod novu lokaciju "{parentName:string}".',
    nb: 'Forberedelsene til {treeName:string}-noden «{nodeName:string}» vil bli plassert under den nye plasseringen «{parentName:string}».',
  },
  cantMoveHere: {
    'en-us': "Can't move this tree node here",
    'ru-ru': 'Невозможно переместить этот узел дерева сюда.',
    'es-es': 'No se puede mover este nodo del árbol aquí',
    'fr-fr': "Impossible de déplacer ce nœud d'arbre ici",
    'uk-ua': 'Неможливо перемістити цей вузол дерева сюди',
    'de-ch': 'Dieser Knoten kann nicht hierhin verschoben werden',
    'pt-br': 'Não é possível mover este nó da árvore para cá.',
    'hr-hr': 'Ovaj čvor stabla ne može se premjestiti ovdje',
    nb: 'Kan ikke flytte denne trenoden hit',
  },
  cantMergeHere: {
    'en-us': "Can't merge this tree node here",
    'ru-ru': 'Невозможно объединить этот узел дерева.',
    'es-es': 'No se puede fusionar este nodo de árbol aquí',
    'fr-fr': "Impossible de fusionner ce nœud d'arbre ici",
    'uk-ua': 'Неможливо об’єднати цей вузол дерева тут',
    'de-ch': 'Dieser Knoten kann hier nicht zusammengelegt werden',
    'pt-br': 'Não é possível mesclar este nó da árvore aqui.',
    'hr-hr': 'Ovaj čvor stabla nije moguće spojiti ovdje',
    nb: 'Kan ikke slå sammen denne trenoden her',
  },
  cantMoveToSynonym: {
    'en-us': "Can't move to a synonym",
    'ru-ru': 'Невозможно перейти к синониму',
    'es-es': 'No se puede mover a un sinónimo',
    'fr-fr': 'Impossible de passer à un synonyme',
    'uk-ua': 'Неможливо перейти до синоніма',
    'de-ch': 'Kann nicht zu einem Synonym verschieben',
    'pt-br': 'Não é possível mover para um sinônimo',
    'hr-hr': 'Ne mogu se premjestiti na sinonim',
    nb: 'Kan ikke flytte til et synonym',
  },
  cantMergeIntoSynonym: {
    'en-us': "Can't merge into synonyms",
    'ru-ru': 'Невозможно объединить в синонимы.',
    'es-es': 'No se pueden fusionar en sinónimos',
    'fr-fr': 'Impossible de fusionner en synonymes',
    'uk-ua': 'Не можна об’єднувати в синоніми',
    'de-ch': 'Kann nicht zu Synonymen zusammenführen',
    'pt-br': 'Não é possível mesclar em sinônimos.',
    'hr-hr': 'Ne može se spojiti u sinonime',
    nb: 'Kan ikke slås sammen til synonymer',
  },
  cantSynonymizeSynonym: {
    'en-us': "Can't synonymize with a synonym",
    'ru-ru': 'Нельзя использовать синоним для обозначения чего-либо.',
    'es-es': 'No se puede sinonimizar con un sinónimo',
    'fr-fr': 'Impossible de synonymiser avec un synonyme',
    'uk-ua': 'Не можна синонімізувати синонім',
    'de-ch': 'Kann nicht mit einem Synonym synonymisiert werden',
    'pt-br': 'Não é possível usar um sinônimo com outro sinônimo.',
    'hr-hr': 'Ne može se sinonimizirati sa sinonimom',
    nb: 'Kan ikke synonymisere med et synonym',
  },
  nodeMoveHintMessage: {
    'en-us': 'Select a new parent for "{nodeName:string}"',
    'ru-ru': 'Выберите нового родителя для "{nodeName:string}"',
    'es-es': 'Seleccione un nuevo padre para "{nodeName:string}"',
    'fr-fr': 'Sélectionnez un nouveau parent pour « {nodeName:string} »',
    'uk-ua': 'Виберіть новий батьківський елемент для "{nodeName:string}"',
    'de-ch': 'Wählen Sie ein neues Elternelement für "{nodeName:string}"',
    'pt-br': 'Selecione um novo pai para "{nodeName:string}"',
    'hr-hr': 'Odaberite novog roditelja za "{nodeName:string}"',
    nb: 'Velg en ny forelder for «{nodeName:string}»',
  },
  mergeNode: {
    'en-us': 'Merge node',
    'ru-ru': 'Слияние узлов',
    'es-es': 'Fusionar el nodo',
    'fr-fr': 'Fusionner le nœud',
    'uk-ua': 'Вузол злиття',
    'de-ch': 'Knoten zusammenführen',
    'pt-br': 'Nó de mesclagem',
    'hr-hr': 'Spoji čvor',
    nb: 'Slå sammen node',
  },
  mergeNodeHere: {
    'en-us': 'Merge "{nodeName:string}" here',
    'ru-ru': 'Объедините "{nodeName:string}" здесь',
    'es-es': 'Fusionar "{nodeName:string}" aquí',
    'fr-fr': 'Fusionner « {nodeName:string} » ici',
    'uk-ua': 'Об\'єднайте "{nodeName:string}" тут',
    'de-ch': 'Führe "{nodeName:string}" hier zusammen',
    'pt-br': 'Mescle "{nodeName:string}" aqui',
    'hr-hr': 'Spoji "{nodeName:string}" ovdje',
    nb: 'Slå sammen «{nodeName:string}» her',
  },
  mergeNodeHintMessage: {
    'en-us': 'Select a new target for "{nodeName:string}" to be merged into',
    'ru-ru': 'Выберите новый целевой объект для слияния "{nodeName:string}".',
    'es-es': 'Seleccione un nuevo objetivo para fusionar "{nodeName:string}"',
    'fr-fr':
      'Sélectionnez une nouvelle cible pour que « {nodeName:string} » y soit fusionné',
    'uk-ua':
      'Виберіть нову ціль для «{nodeName:string}», у яку потрібно об’єднати',
    'de-ch': 'Wähle ein neues Ziel um "{nodeName:string}" zusammenzuführen',
    'pt-br':
      'Selecione um novo destino para "{nodeName:string}" a ser mesclado.',
    'hr-hr': 'Odaberite novi cilj za "{nodeName:string}" koji će se spojiti s',
    nb: 'Velg et nytt mål som «{nodeName:string}» skal slås sammen med',
  },
  bulkMoveNodeHintMessage: {
    'en-us':
      'Select a new target for "{nodeName:string}" preparations to be moved into',
    'de-ch':
      'Wählen Sie ein neues Ziel für die "{nodeName:string}"-Vorbereitungen aus, die verschoben werden sollen.',
    'es-es':
      'Seleccione un nuevo objetivo para los preparaciones "{nodeName:string}" que se trasladarán a',
    'fr-fr':
      'Sélectionnez une nouvelle cible pour que « {nodeName:string} » y soit déplacé',
    'ru-ru':
      'Выберите новую цель для перемещения подготовительных работ "{nodeName:string}".',
    'uk-ua':
      'Виберіть нову ціль для препаратів "{nodeName:string}", до якої потрібно переміститися',
    'pt-br':
      'Selecione um novo alvo para os preparativos "{nodeName:string}" a serem movidos para',
    'hr-hr':
      'Odaberite novi cilj za pripreme "{nodeName:string}" koje će se premjestiti u',
    nb: 'Velg et nytt mål for "{nodeName:string}"-preparatene som skal flyttes til',
  },
  mergeNodeMessage: {
    'en-us':
      'All references to {treeName:string} node "{nodeName:string}" will be replaced with "{parentName:string}", and all descendants of "{nodeName:string}" will be moved to "{parentName:string}" with any descendants matching in name and rank being themselves merged recursively.',
    'ru-ru':
      'Все ссылки на узел {treeName:string} "{nodeName:string}" будут заменены на "{parentName:string}", а все потомки "{nodeName:string}" будут перемещены в "{parentName:string}", при этом любые потомки, совпадающие по имени и рангу, будут рекурсивно объединены.',
    'es-es':
      'Todas las referencias al nodo {treeName:string} "{nodeName:string}" serán reemplazadas por "{parentName:string}", todos los descendientes de "{nodeName:string}" se moverán a "{parentName:string}" y todos los descendientes que coincidan en nombre y rango se fusionarán de forma recursiva.',
    'fr-fr':
      'Toutes les références au nœud {treeName:string} "{nodeName:string}" seront remplacées par "{parentName:string}", et tous les descendants de "{nodeName:string}" seront déplacés vers "{parentName:string}" les descendants dont le nom et le rang correspondent seront eux-mêmes fusionnés de manière récursive.',
    'uk-ua':
      "Усі посилання на вузол {treeName:string} «{nodeName:string}» буде замінено на «{parentName:string}», а всі нащадки «{nodeName:string}» буде переміщено до «{parentName:string}», а будь-які нащадки, що відповідають імені та рангу, будуть самі собою об'єднані рекурсивно.",
    'de-ch':
      'Alle Referenzen zu {treeName:string} "{nodeName:string}" werden mit "{parentName:string}" ersetzt. Alle Nachkommen von "{nodeName:string}" werden nach "{parentName:string}" verschoben, wobei alle Nachkommen, die in Name und Rang übereinstimmen, selbst rekursiv zusammengeführt werden.',
    'pt-br':
      'Todas as referências ao nó {treeName:string} "{nodeName:string}" serão substituídas por "{parentName:string}", e todos os descendentes de "{nodeName:string}" serão movidos para "{parentName:string}", com quaisquer descendentes correspondentes em nome e classificação sendo eles próprios mesclados recursivamente.',
    'hr-hr':
      'Sve reference na čvor {treeName:string} "{nodeName:string}" bit će zamijenjene s "{parentName:string}", a svi potomci "{nodeName:string}" bit će premješteni u "{parentName:string}", a svi potomci koji se podudaraju po imenu i rangu bit će rekurzivno spojeni.',
    nb: 'Alle referanser til {treeName:string}-noden «{nodeName:string}» vil bli erstattet med «{parentName:string}», og alle etterkommere av «{nodeName:string}» vil bli flyttet til «{parentName:string}», der eventuelle etterkommere som samsvarer i navn og rang blir slått sammen rekursivt.',
  },
  synonymizeNode: {
    'en-us': 'Synonymize node',
    'ru-ru': 'Синонимизировать узел',
    'es-es': 'Sinonimizar nodo',
    'fr-fr': 'Synonymiser le nœud',
    'uk-ua': 'Синонімізувати вузол',
    'de-ch': 'Knoten synonymisieren',
    'pt-br': 'Sinônimo de nó',
    'hr-hr': 'Sinonimiziraj čvor',
    nb: 'Synonymiser node',
  },
  makeSynonym: {
    'en-us': 'Make {nodeName:string} a synonym of {synonymName:string}',
    'ru-ru': 'Сделайте {nodeName:string} синонимом {synonymName:string}',
    'es-es': 'Hacer de {nodeName:string} un sinónimo de {synonymName:string}',
    'fr-fr': 'Faire de {nodeName:string} un synonyme de {synonymName:string}',
    'uk-ua': 'Зробити {nodeName:string} синонімом {synonymName:string}',
    'de-ch':
      'Aus {nodeName:string} ein Synonym von {synonymName:string} machen',
    'pt-br': 'Faça de {nodeName:string} um sinônimo de {synonymName:string}',
    'hr-hr': 'Učini {nodeName:string} sinonimom za {synonymName:string}',
    nb: 'Gjør {nodeName:string} til et synonym for {synonymName:string}',
  },
  synonymizeNodeHintMessage: {
    'en-us': 'Select a target for "{nodeName:string}" to be synonymized to',
    'ru-ru':
      'Выберите целевой объект для "{nodeName:string}", который будет синонимизирован.',
    'es-es':
      'Seleccione un objetivo para que "{nodeName:string}" se convierta en sinónimo',
    'fr-fr':
      'Sélectionnez une cible pour que « {nodeName:string} » y soit synonymisé',
    'uk-ua': 'Виберіть ціль для "{nodeName:string}", який буде синонімічним',
    'de-ch': 'Wähle Ziel um "{nodeName:string}" daran zu synonymisieren',
    'pt-br': 'Selecione um alvo para "{nodeName:string}" a ser sinônimo de',
    'hr-hr': 'Odaberite cilj za koji će se "{nodeName:string}" sinonimizirati',
    nb: 'Velg et mål som «{nodeName:string}» skal synonymiseres til',
  },
  synonymizeMessage: {
    'en-us':
      'The {treeName:string} node "{nodeName:string}" will be made a synonym of "{synonymName:string}".',
    'ru-ru':
      'Узел {treeName:string} "{nodeName:string}" будет синонимом "{synonymName:string}".',
    'es-es':
      'El nodo {treeName:string} "{nodeName:string}" se convertirá en sinónimo de "{synonymName:string}".',
    'fr-fr':
      'Le nœud {treeName:string} «{nodeName:string}» sera synonymisé avec «{synonymName:string}».',
    'de-ch':
      'Der {treeName:string}-Knoten "{nodeName:string}" wird zu einem Synonym von "{synonymName:string}".',
    'uk-ua':
      'Вузол {treeName:string} "{nodeName:string}" стане синонімом "{synonymName:string}".',
    'pt-br':
      'O nó {treeName:string} "{nodeName:string}" será transformado em sinônimo de "{synonymName:string}".',
    'hr-hr':
      'Čvor {treeName:string} "{nodeName:string}" bit će sinonim za "{synonymName:string}".',
    nb: '{treeName:string}-noden «{nodeName:string}» vil bli gjort til et synonym for «{synonymName:string}».',
  },
  desynonymizeNode: {
    'en-us': 'Desynonymize node',
    'ru-ru': 'Десинонимизировать узел',
    'es-es': 'Desinonimizar nodo',
    'fr-fr': 'Désynonymiser le nœud',
    'uk-ua': 'Десинонімізувати вузол',
    'de-ch': 'Knoten desynonymisieren',
    'pt-br': 'Dessinonizar nó',
    'hr-hr': 'Desinonimiziraj čvor',
    nb: 'Desynonymiser node',
  },
  desynonymizeNodeMessage: {
    'en-us':
      '"{nodeName:string}" will no longer be a synonym of "{synonymName:string}".',
    'ru-ru':
      '"{nodeName:string}" больше не будет синонимом "{synonymName:string}".',
    'es-es':
      '"{nodeName:string}" ya no será sinónimo de "{synonymName:string}".',
    'fr-fr':
      '« {nodeName:string} » ne sera plus synonyme de « {synonymName:string} ».',
    'de-ch':
      '"{nodeName:string}" wird nicht mehr ein Synonym von "{synonymName:string}" sein.',
    'uk-ua':
      '"{nodeName:string}" більше не буде синонімом "{synonymName:string}".',
    'pt-br':
      '"{nodeName:string}" deixará de ser sinônimo de "{synonymName:string}".',
    'hr-hr':
      '"{nodeName:string}" više neće biti sinonim za "{synonymName:string}".',
    nb: '«{nodeName:string}» vil ikke lenger være et synonym for «{synonymName:string}».',
  },
  acceptedName: {
    'en-us': 'Preferred: {name:string}',
    'ru-ru': 'Предпочтительный вариант: {name:string}',
    'es-es': 'Preferido: {name:string}',
    'fr-fr': 'Préféré : {name:string}',
    'uk-ua': 'Бажано: {name:string}',
    'de-ch': 'Bevorzugt: {name:string}',
    'pt-br': 'Preferencial: {name:string}',
    'hr-hr': 'Preferirano: {name:string}',
    nb: 'Foretrukket: {name:string}',
  },
  synonyms: {
    'en-us': 'Synonyms: {names:string}',
    'de-ch': 'Synonyme: {names:string}',
    'es-es': 'Sinónimos: {names:string}',
    'fr-fr': 'Synonymes : {names:string}',
    'ru-ru': 'Синонимы: {names:string}',
    'uk-ua': 'Синоніми: {names:string}',
    'pt-br': 'Sinônimos: {names:string}',
    'hr-hr': 'Sinonimi: {names:string}',
    nb: 'Synonymer: {names:string}',
  },
  treeViewTitle: {
    'en-us': '{treeName:string} Tree',
    'ru-ru': '{treeName:string} Дерево',
    'es-es': '{treeName:string} Árbol',
    'fr-fr': '{treeName:string} Arbre',
    'uk-ua': '{treeName:string} Дерево',
    'de-ch': '{treeName:string} Baum',
    'pt-br': '{treeName:string} Árvore',
    'hr-hr': '{treeName:string} Stablo',
    nb: '{treeName:string} Tre',
  },
  searchTreePlaceholder: {
    'en-us': 'Search Tree',
    'ru-ru': 'Дерево поиска',
    'es-es': 'Árbol de búsqueda',
    'fr-fr': "Rechercher dans l'arbre",
    'uk-ua': 'Дерево пошуку',
    'de-ch': 'Baum durchsuchen',
    'pt-br': 'Árvore de pesquisa',
    'hr-hr': 'Stablo pretraživanja',
    nb: 'Søketre',
  },
  opened: {
    'en-us': 'Opened',
    'ru-ru': 'Открыто',
    'es-es': 'Abierto',
    'fr-fr': 'Ouvert',
    'uk-ua': 'Відкрито',
    'de-ch': 'Geöffnet',
    'pt-br': 'Aberto',
    'hr-hr': 'Otvoreno',
    nb: 'Åpnet',
  },
  closed: {
    'en-us': 'Closed',
    'ru-ru': 'Закрыто',
    'es-es': 'Cerrado',
    'fr-fr': 'Fermé',
    'uk-ua': 'ЗАЧИНЕНО',
    'de-ch': 'Geschlossen',
    'pt-br': 'Fechado',
    'hr-hr': 'Zatvoreno',
    nb: 'Lukket',
  },
  leafNode: {
    'en-us': 'Leaf Node',
    'ru-ru': 'Листовой узел',
    'es-es': 'Nodo de hoja',
    'fr-fr': 'Nœud feuille',
    'uk-ua': 'Листковий вузол',
    'de-ch': 'Blattknoten',
    'pt-br': 'Nó foliar',
    'hr-hr': 'Čvor lista',
    nb: 'Bladknute',
  },
  nodeStats: {
    comment: "Used to show tree node's direct and indirect usages",
    'en-us': '({directCount:number|formatted}, {childCount:number|formatted})',
    'ru-ru': '({directCount:number|formatted}, {childCount:number|formatted})',
    'es-es': '({directCount:number|formatted}, {childCount:number|formatted})',
    'fr-fr': '({directCount:number|formatted}, {childCount:number|formatted})',
    'uk-ua': "({directCount:number|formatted}', {childCount:number|formatted})",
    'de-ch': '({directCount:number|formatted}, {childCount:number|formatted})',
    'pt-br': '({directCount:number|formatted}, {childCount:number|formatted})',
    'hr-hr': '({directCount:number|formatted}, {childCount:number|formatted})',
    nb: '({directCount:number|formatted}, {childCount:number|formatted})',
  },
  leafNodeStats: {
    comment: "Used to show leaf tree node's direct usages",
    'en-us': '({directCount:number|formatted})',
    'ru-ru': '({directCount:number|formatted})',
    'es-es': '({directCount:number|formatted})',
    'fr-fr': '({directCount:number|formatted})',
    'uk-ua': "({directCount:number|formatted})'",
    'de-ch': '({directCount:number|formatted})',
    'pt-br': '({directCount:number|formatted})',
    'hr-hr': '({directCount:number|formatted})',
    nb: '({directCount:number|formatted})',
  },
  directCollectionObjectCount: {
    comment: 'Example: Direct Collection Object count',
    'en-us': 'Direct {collectionObjectTable:string} Count',
    'ru-ru': 'Прямой {collectionObjectTable:string} подсчет',
    'es-es': 'Recuento directo {collectionObjectTable:string}',
    'de-ch': 'Direkte {collectionObjectTable:string} Anzahl',
    'fr-fr': 'Compte direct {collectionObjectTable:string}',
    'uk-ua': 'Прямий підрахунок {collectionObjectTable:string}',
    'pt-br': 'Contagem direta {collectionObjectTable:string}',
    'hr-hr': 'Izravno {collectionObjectTable:string} brojanje',
    nb: 'Direkte {collectionObjectTable:string}-telling',
  },
  indirectCollectionObjectCount: {
    comment: 'Example: Indirect Collection Object count',
    'en-us': 'Indirect {collectionObjectTable:string} Count',
    'ru-ru': 'Косвенный {collectionObjectTable:string} подсчет',
    'es-es': 'Recuento indirecto {collectionObjectTable:string}',
    'fr-fr': 'Compte indirect {collectionObjectTable:string}',
    'uk-ua': 'Непрямий підрахунок {collectionObjectTable:string}',
    'de-ch': 'Indirekte {collectionObjectTable:string} Anzahl',
    'pt-br': 'Contagem indireta {collectionObjectTable:string}',
    'hr-hr': 'Neizravno {collectionObjectTable:string} brojanje',
    nb: 'Indirekte {collectionObjectTable:string}-telling',
  },
  geologicTimePeriodLine: {
    comment: `
      Tooltip line showing a period with uncertainty. Example:
      Start: 298.9 (± 0.15 Ma)
    `,
    'en-us': '{label:string}: {value:string} {suffix:string}',
    'de-ch': '{label:string}: {value:string} {suffix:string}',
    'es-es': '{label:string}: {value:string} {suffix:string}',
    'fr-fr': '{label:string} : {value:string} {suffix:string}',
    'hr-hr': '{label:string}: {value:string} {suffix:string}',
    nb: '{label:string}: {value:string} {suffix:string}',
    'pt-br': '{label:string}: {value:string} {suffix:string}',
    'ru-ru': '{label:string}: {value:string} {suffix:string}',
    'uk-ua': '{label:string}: {value:string} {suffix:string}',
  },
  megaAnnum: {
    comment: 'Abbreviation for millions of years (Mega annum)',
    'en-us': 'Ma',
    'de-ch': 'Ma',
    'es-es': 'Mamá',
    'fr-fr': 'Ma',
    'hr-hr': 'Ma',
    nb: 'Mamma',
    'pt-br': 'Ma',
    'ru-ru': 'Ма',
    'uk-ua': 'Ма',
  },
  biostratFilter: {
    'en-us': 'Filter by biostratigraphy',
    'de-ch': 'Filterung nach Biostratigraphie',
    'es-es': 'Filtrar por bioestratigrafía',
    'fr-fr': 'Filtrer par biostratigraphie',
    'hr-hr': 'Filtriraj po biostratigrafiji',
    nb: 'Filtrer etter biostratigrafi',
    'pt-br': 'Filtrar por bioestratigrafia',
    'ru-ru': 'Фильтрация по биостратиграфии',
    'uk-ua': 'Фільтрувати за біостратиграфією',
  },
  biostratAll: {
    'en-us': 'All Nodes',
    'de-ch': 'Alle Knoten',
    'es-es': 'Todos los nodos',
    'fr-fr': 'Tous les nœuds',
    'hr-hr': 'Svi čvorovi',
    nb: 'Alle noder',
    'pt-br': 'Todos os nós',
    'ru-ru': 'Все узлы',
    'uk-ua': 'Усі вузли',
  },
  biostratChrono: {
    'en-us': 'Chronostratigraphy',
    'de-ch': 'Chronostratigraphie',
    'es-es': 'Cronoestratigrafía',
    'fr-fr': 'Chronostratigraphie',
    'hr-hr': 'Kronostratigrafija',
    nb: 'Kronostratigrafi',
    'pt-br': 'Cronoestratigrafia',
    'ru-ru': 'Хроностратиграфия',
    'uk-ua': 'Хроностратиграфія',
  },
  biostratBio: {
    'en-us': 'Biostratigraphy',
    'de-ch': 'Biostratigraphie',
    'es-es': 'Bioestratigrafía',
    'fr-fr': 'Biostratigraphie',
    'hr-hr': 'Biostratigrafija',
    nb: 'Biostratigrafi',
    'pt-br': 'Bioestratigrafia',
    'ru-ru': 'Биостратиграфия',
    'uk-ua': 'Біостратиграфія',
  },
  editRanks: {
    'en-us': 'Edit Ranks',
    'ru-ru': 'Редактировать ранги',
    'es-es': 'Editar rangos',
    'fr-fr': 'Modifier les rangs',
    'uk-ua': 'Редагувати ранги',
    'de-ch': 'Positionen bearbeiten',
    'pt-br': 'Editar classificações',
    'hr-hr': 'Uredi rangove',
    nb: 'Rediger rangeringer',
  },
  resourceToDelete: {
    'en-us': 'This will permanently delete the following resource',
    'es-es': 'Esto eliminará permanentemente el siguiente recurso',
    'fr-fr': 'Cela supprimera définitivement la ressource suivante',
    'ru-ru': 'Это приведет к безвозвратному удалению следующего ресурса.',
    'uk-ua': 'Це призведе до остаточного видалення наступного ресурсу',
    'de-ch': 'Dadurch wird die folgende Ressource dauerhaft gelöscht',
    'pt-br': 'Isso excluirá permanentemente o seguinte recurso.',
    'hr-hr': 'Ovim će se trajno izbrisati sljedeći resurs',
    nb: 'Dette vil slette følgende ressurs permanent',
  },
  associatedNodesOnly: {
    'en-us': 'Show only nodes with associated objects',
    'de-ch': 'Es werden nur Knoten mit zugehörigen Objekten angezeigt.',
    'es-es': 'Mostrar solo nodos con objetos asociados',
    'fr-fr': 'Afficher uniquement les nœuds avec des objets associés',
    'ru-ru': 'Отображать только узлы, связанные с объектами.',
    'uk-ua': "Показувати лише вузли з пов'язаними об'єктами",
    'pt-br': 'Mostrar apenas os nós com objetos associados.',
    'hr-hr': 'Prikaži samo čvorove s povezanim objektima',
    nb: 'Vis bare noder med tilhørende objekter',
  },
  splitView: {
    'en-us': 'Split View',
    'de-ch': 'Geteilte Sicht',
    'es-es': 'Vista dividida',
    'fr-fr': 'Vue fractionnée',
    'ru-ru': 'Разделенный экран',
    'uk-ua': 'Розділений вид',
    'pt-br': 'Visão dividida',
    'hr-hr': 'Podijeljeni prikaz',
    nb: 'Delt visning',
  },
  horizontal: {
    'en-us': 'Horizontal',
    'de-ch': 'Horizontal',
    'es-es': 'Horizontal',
    'fr-fr': 'Horizontal',
    'ru-ru': 'Горизонтальный',
    'uk-ua': 'Горизонтальний',
    'pt-br': 'Horizontal',
    'hr-hr': 'Horizontalno',
    nb: 'Horisontal',
  },
  vertical: {
    'en-us': 'Vertical',
    'de-ch': 'Vertikal',
    'es-es': 'Vertical',
    'fr-fr': 'Vertical',
    'ru-ru': 'Вертикальный',
    'uk-ua': 'Вертикальний',
    'pt-br': 'Vertical',
    'hr-hr': 'Vertikalno',
    nb: 'Vertikal',
  },
  synchronize: {
    'en-us': 'Synchronize',
    'de-ch': 'Synchronisieren',
    'es-es': 'Sincronizar',
    'fr-fr': 'Synchroniser',
    'uk-ua': 'Синхронізувати',
    'ru-ru': 'Синхронизировать',
    'pt-br': 'Sincronizar',
    'hr-hr': 'Sinkroniziraj',
    nb: 'Synkroniser',
  },
  addNewRank: {
    'en-us': 'Add New Rank',
    'de-ch': 'Neuen Rang hinzufügen',
    'es-es': 'Añadir nuevo rango',
    'fr-fr': 'Ajouter un nouveau rang',
    'ru-ru': 'Добавить новый ранг',
    'uk-ua': 'Додати новий ранг',
    'pt-br': 'Adicionar novo cargo',
    'hr-hr': 'Dodaj novi rang',
    nb: 'Legg til ny rangering',
  },
  chooseParentRank: {
    'en-us': 'Choose Parent Rank',
    'de-ch': 'Übergeordneten Rang auswählen',
    'es-es': 'Elija el rango de los padres',
    'fr-fr': 'Choisir le rang du parent',
    'ru-ru': 'Выберите родительский ранг',
    'uk-ua': 'Виберіть батьківський рейтинг',
    'pt-br': 'Escolha a classificação principal',
    'hr-hr': 'Odaberite roditeljski rang',
    nb: 'Velg foreldrerangering',
  },
  moveItems: {
    'en-us': 'Move Items',
    'de-ch': 'Gegenstände verschieben',
    'es-es': 'Mover elementos',
    'fr-fr': 'Déplacer des éléments',
    'ru-ru': 'Переместить предметы',
    'uk-ua': 'Переміщення елементів',
    'pt-br': 'Mover itens',
    'hr-hr': 'Premještanje stavki',
    nb: 'Flytt elementer',
  },
  addTree: {
    'en-us': 'Add Tree',
    'de-ch': 'Baum hinzufügen',
    'es-es': 'Agregar árbol',
    'fr-fr': 'Ajouter un arbre',
    'pt-br': 'Adicionar árvore',
    'ru-ru': 'Добавить дерево',
    'uk-ua': 'Додати дерево',
    'hr-hr': 'Dodaj stablo',
    nb: 'Legg til tre',
  },
  treePicker: {
    'en-us': 'Tree Picker',
    'de-ch': 'Baumpflücker',
    'es-es': 'Selector de árboles',
    'fr-fr': "Sélectionner l'arbre",
    'pt-br': 'apanhador de árvores',
    'ru-ru': 'Сборщик деревьев',
    'uk-ua': 'Збирач дерев',
    'hr-hr': 'Berač drveća',
    nb: 'Treplukker',
  },
  botany: {
    'en-us': 'Botany',
    'de-ch': 'Botanik',
    'es-es': 'Botánica',
    'fr-fr': 'Botanique',
    'pt-br': 'Botânica',
    'ru-ru': 'Ботаника',
    'uk-ua': 'Ботаніка',
    'hr-hr': 'Botanika',
    nb: 'Botanikk',
  },
  entomology: {
    'en-us': 'Entomology',
    'de-ch': 'Entomologie',
    'es-es': 'Entomología',
    'fr-fr': 'Entomologie',
    'pt-br': 'Entomologia',
    'ru-ru': 'Энтомология',
    'uk-ua': 'Ентомологія',
    'hr-hr': 'Entomologija',
    nb: 'Entomologi',
  },
  herpetology: {
    'en-us': 'Herpetology',
    'de-ch': 'Herpetologie',
    'es-es': 'Herpetología',
    'fr-fr': 'Herpétologie',
    'pt-br': 'Herpetologia',
    'ru-ru': 'Герпетология',
    'uk-ua': 'Герпетологія',
    'hr-hr': 'Herpetologija',
    nb: 'Herpetologi',
  },
  ichthyology: {
    'en-us': 'Ichthyology',
    'de-ch': 'Fischkunde',
    'es-es': 'Ictiología',
    'fr-fr': 'Ichtyologie',
    'pt-br': 'Ictiologia',
    'ru-ru': 'Ихтиология',
    'uk-ua': 'Іхтіологія',
    'hr-hr': 'Ihtiologija',
    nb: 'Iktyologi',
  },
  invertpaleo: {
    'en-us': 'Invertebrate Paleontology',
    'de-ch': 'Wirbellosenpaläontologie',
    'es-es': 'Paleontología de invertebrados',
    'fr-fr': 'Paléontologie des invertébrés',
    'pt-br': 'Paleontologia de Invertebrados',
    'ru-ru': 'Палеонтология беспозвоночных',
    'uk-ua': 'Палеонтологія безхребетних',
    'hr-hr': 'Paleontologija beskralježnjaka',
    nb: 'Paleontologi for virvelløse dyr',
  },
  invertzoo: {
    'en-us': 'Invertebrate Zoology',
    'de-ch': 'Wirbellosenzoologie',
    'es-es': 'Zoología de invertebrados',
    'fr-fr': 'Zoologie des invertébrés',
    'pt-br': 'Zoologia de Invertebrados',
    'ru-ru': 'Зоология беспозвоночных',
    'uk-ua': 'Зоологія безхребетних',
    'hr-hr': 'Zoologija beskralježnjaka',
    nb: 'Virvelløse dyrs zoologi',
  },
  mammalogy: {
    'en-us': 'Mammalogy',
    'de-ch': 'Mammalogie',
    'es-es': 'Mastozoología',
    'fr-fr': 'Mammalogie',
    'pt-br': 'Mamalogia',
    'ru-ru': 'Маммология',
    'uk-ua': 'Мамалогія',
    'hr-hr': 'Nauka o sisarima',
    nb: 'Mammologi',
  },
  ornithology: {
    'en-us': 'Ornithology',
    'de-ch': 'Vogelkunde',
    'es-es': 'Ornitología',
    'fr-fr': 'Ornithologie',
    'pt-br': 'Ornitologia',
    'ru-ru': 'Орнитология',
    'uk-ua': 'Орнітологія',
    'hr-hr': 'Ornitologija',
    nb: 'Ornitologi',
  },
  paleobot: {
    'en-us': 'Paleobotany',
    'de-ch': 'Paläobotanik',
    'es-es': 'Paleobotánica',
    'fr-fr': 'Paléobotanique',
    'pt-br': 'Paleobotânica',
    'ru-ru': 'Палеоботаника',
    'uk-ua': 'Палеоботаніка',
    'hr-hr': 'Paleobotanika',
    nb: 'Paleobotanikk',
  },
  vascplant: {
    'en-us': 'Vascular Plants',
    'de-ch': 'Gefäßpflanzen',
    'es-es': 'Plantas vasculares',
    'fr-fr': 'Plantes vasculaires',
    'pt-br': 'Plantas Vasculares',
    'ru-ru': 'Сосудистые растения',
    'uk-ua': 'Судинні рослини',
    'hr-hr': 'Vaskularne biljke',
    nb: 'Karplanter',
  },
  vertpaleo: {
    'en-us': 'Vertebrate Paleontology',
    'de-ch': 'Wirbeltierpaläontologie',
    'es-es': 'Paleontología de vertebrados',
    'fr-fr': 'Paléontologie des vertébrés',
    'pt-br': 'Paleontologia de Vertebrados',
    'ru-ru': 'Палеонтология позвоночных',
    'uk-ua': 'Палеонтологія хребетних',
    'hr-hr': 'Paleontologija kralježnjaka',
    nb: 'Virveldyrpaleontologi',
  },
  emptyTree: {
    'en-us': 'Empty Tree',
    'de-ch': 'Leerer Baum',
    'es-es': 'Árbol vacío',
    'fr-fr': 'Arbre vide',
    'pt-br': 'Árvore Vazia',
    'ru-ru': 'Пустое дерево',
    'uk-ua': 'Порожнє дерево',
    'hr-hr': 'Prazno stablo',
    nb: 'Tomt tre',
  },
  minerals: {
    'en-us': 'Minerals',
    'de-ch': 'Mineralien',
    'es-es': 'Minerales',
    'fr-fr': 'Minéraux',
    'pt-br': 'Minerais',
    'ru-ru': 'Минералы',
    'uk-ua': 'Мінерали',
    'hr-hr': 'Minerali',
    nb: 'Mineraler',
  },
  rocks: {
    'en-us': 'Rocks',
    'de-ch': 'Felsen',
    'es-es': 'Rocas',
    'fr-fr': 'Roches',
    'pt-br': 'Rochas',
    'ru-ru': 'Камни',
    'uk-ua': 'Скелі',
    'hr-hr': 'Stijene',
    nb: 'Steiner',
  },
  meteorites: {
    'en-us': 'Meteorites',
    'de-ch': 'Meteoriten',
    'es-es': 'Meteoritos',
    'fr-fr': 'Météorites',
    'pt-br': 'Meteoritos',
    'ru-ru': 'Метеориты',
    'uk-ua': 'Метеорити',
    'hr-hr': 'Meteoriti',
    nb: 'Meteoritter',
  },
  emptyTrees: {
    'en-us': 'Empty Trees',
    'de-ch': 'Leere Bäume',
    'es-es': 'Árboles vacíos',
    'fr-fr': 'Arbres vides',
    'hr-hr': 'Prazna stabla',
    'pt-br': 'Árvores vazias',
    'ru-ru': 'Пустые деревья',
    'uk-ua': 'Порожні дерева',
    nb: 'Tomme trær',
  },
  populatedTrees: {
    'en-us': 'Populated Trees',
    'de-ch': 'Besiedelte Bäume',
    'es-es': 'Árboles poblados',
    'fr-fr': 'Arbres peuplés',
    'hr-hr': 'Naseljena stabla',
    'pt-br': 'Árvores Populadas',
    'ru-ru': 'Заселенные деревья',
    'uk-ua': 'Населені дерева',
    nb: 'Befolkede trær',
  },
  defaultTreeCreationStarted: {
    'en-us': 'Default Tree Creation Started',
    'de-ch': 'Erstellung des Standardbaums gestartet',
    'es-es': 'Creación de árbol predeterminada iniciada',
    'fr-fr': "Création de l'arbre par défaut démarrée",
    'hr-hr': 'Započeto je stvaranje zadanog stabla',
    'pt-br': 'Criação da árvore padrão iniciada',
    'ru-ru': 'Началось создание дерева по умолчанию.',
    'uk-ua': 'Розпочато створення дерева за замовчуванням',
    nb: 'Standard treoppretting startet',
  },
  defaultTreeCreationStartedDescription: {
    'en-us':
      'The tree will be created in the background. You will be notified once it is completed.',
    'de-ch':
      'Der Baum wird im Hintergrund erstellt. Sie werden benachrichtigt, sobald er fertiggestellt ist.',
    'es-es':
      'El árbol se creará en segundo plano. Recibirás una notificación cuando esté terminado.',
    'fr-fr':
      "L'arbre sera créé en arrière-plan. Vous serez averti une fois la création terminée.",
    'hr-hr':
      'Stablo će se stvoriti u pozadini. Bit ćete obaviješteni kada bude dovršeno.',
    'pt-br':
      'A árvore será criada em segundo plano. Você será notificado assim que o processo for concluído.',
    'ru-ru':
      'Дерево будет создано в фоновом режиме. Вы получите уведомление, как только процесс завершится.',
    'uk-ua':
      'Дерево буде створено у фоновому режимі. Ви отримаєте сповіщення, коли це буде завершено.',
    nb: 'Treet vil bli opprettet i bakgrunnen. Du vil bli varslet når det er fullført.',
  },
  defaultTreeTaskStarted: {
    'en-us': 'The default tree creation process has started.',
    'de-ch':
      'Der Standardprozess zur Erstellung der Baumstruktur wurde gestartet.',
    'es-es': 'Se ha iniciado el proceso de creación del árbol predeterminado.',
    'fr-fr': "Le processus de création d'arbre par défaut a démarré.",
    'hr-hr': 'Započet je proces stvaranja zadanog stabla.',
    'pt-br': 'O processo padrão de criação da árvore foi iniciado.',
    'ru-ru': 'Начался процесс создания дерева по умолчанию.',
    'uk-ua': 'Процес створення дерева за замовчуванням розпочато.',
    nb: 'Standardprosessen for oppretting av treet har startet.',
  },
  defaultTreeTaskFailed: {
    'en-us': 'Default tree creation failed.',
    'de-ch': 'Die Erstellung des Standardbaums ist fehlgeschlagen.',
    'es-es': 'Falló la creación del árbol predeterminado.',
    'fr-fr': "La création de l'arborescence par défaut a échoué.",
    'hr-hr': 'Izrada zadanog stabla nije uspjela.',
    'pt-br': 'A criação da árvore padrão falhou.',
    'ru-ru': 'Создание дерева по умолчанию не удалось.',
    'uk-ua': 'Не вдалося створити дерево за замовчуванням.',
    nb: 'Oppretting av standardtre mislyktes.',
  },
  defaultTreeTaskCancelled: {
    'en-us': 'Default tree creation was cancelled.',
    'de-ch': 'Die Erstellung des Standardbaums wurde abgebrochen.',
    'es-es': 'Se canceló la creación del árbol predeterminado.',
    'fr-fr': "La création de l'arbre par défaut a été annulée.",
    'hr-hr': 'Izrada zadanog stabla je otkazana.',
    'pt-br': 'A criação da árvore padrão foi cancelada.',
    'ru-ru': 'Создание дерева по умолчанию было отменено.',
    'uk-ua': 'Створення дерева за замовчуванням скасовано.',
    nb: 'Standardoppretting av tre ble avbrutt.',
  },
  defaultTreeTaskCompleted: {
    'en-us': 'Default tree creation completed successfully.',
    'de-ch':
      'Die Erstellung der Standardbaumstruktur wurde erfolgreich abgeschlossen.',
    'es-es': 'La creación del árbol predeterminado se completó correctamente.',
    'fr-fr': "La création de l'arbre par défaut s'est terminée avec succès.",
    'hr-hr': 'Izrada zadanog stabla uspješno je dovršena.',
    'pt-br': 'Criação da árvore padrão concluída com sucesso.',
    'ru-ru': 'Создание дерева по умолчанию успешно завершено.',
    'uk-ua': 'Створення дерева за замовчуванням успішно завершено.',
    nb: 'Standardtreoppretting fullført.',
  },
  defaultTreeCreationProgress: {
    comment: 'E.x, Creating tree record 999/1,000',
    'en-us':
      'Creating tree record {current:number|formatted}/{total:number|formatted}',
    'de-ch':
      'Erstelle Baumdatensatz {current:number|formatted}/{total:number|formatted}',
    'es-es':
      'Creando registro de árbol {current:number|formatted}/{total:number|formatted}',
    'fr-fr':
      "Création de l'enregistrement d'arbre {current:number|formatted}/{total:number|formatted}",
    'hr-hr':
      'Izrada zapisa stabla {current:number|formatted}/{total:number|formatted}',
    'pt-br':
      'Criando registro de árvore {current:number|formatted}/{total:number|formatted}',
    'ru-ru':
      'Создание записи дерева {current:number|formatted}/{total:number|formatted}',
    'uk-ua':
      'Створення запису дерева {current:number|formatted}/{total:number|formatted}',
    nb: 'Oppretter trepost {current:number|formatted}/{total:number|formatted}',
  },
  defaultTreeCreationLoadingMessage: {
    'en-us': 'Default tree creation is in progress, please wait...',
    'de-ch': 'Die Erstellung der Standardbaumstruktur läuft, bitte warten...',
    'es-es':
      'La creación del árbol predeterminado está en curso, por favor espere...',
    'fr-fr':
      "La création de l'arborescence par défaut est en cours, veuillez patienter...",
    'hr-hr': 'Izrada zadanog stabla je u tijeku, molimo pričekajte...',
    'pt-br': 'A criação da árvore padrão está em andamento, aguarde...',
    'ru-ru':
      'Идет процесс создания дерева по умолчанию, пожалуйста, подождите...',
    'uk-ua': 'Триває створення дерева за замовчуванням, зачекайте...',
    nb: 'Standard treoppretting pågår, vennligst vent ...',
  },
  missingRanks: {
    'en-us': 'Missing Ranks',
    'de-ch': 'Fehlende Ränge',
    'es-es': 'Rangos desaparecidos',
    'fr-fr': 'Rangs manquants',
    'hr-hr': 'Nedostajući rangovi',
    'pt-br': 'Patentes Ausentes',
    'ru-ru': 'Отсутствующие звания',
    'uk-ua': 'Відсутні ранги',
    nb: 'Manglende rangeringer',
  },
  missingRanksDescription: {
    'en-us':
      'The populated tree you selected to download contains records in ranks that are missing from your tree.',
    'de-ch':
      'Der von Ihnen zum Herunterladen ausgewählte gefüllte Stammbaum enthält Datensätze in Rängen, die in Ihrem Stammbaum fehlen.',
    'es-es':
      'El árbol genealógico que seleccionaste para descargar contiene registros en rangos que faltan en tu árbol.',
    'fr-fr':
      "L'arbreque vous avez sélectionné pour le téléchargement contient des enregistrements à des rangs qui sont absents de votre arbre.",
    'hr-hr':
      'Popunjeno stablo koje ste odabrali za preuzimanje sadrži zapise u rangovima koji nedostaju u vašem stablu.',
    'pt-br':
      'A árvore genealógica que você selecionou para baixar contém registros em níveis hierárquicos que estão faltando na sua árvore.',
    'ru-ru':
      'Выбранное вами для загрузки дерево содержит записи в рангах, отсутствующих в вашем дереве.',
    'uk-ua':
      'Заповнене дерево, яке ви вибрали для завантаження, містить записи в рангах, яких бракує у вашому дереві.',
    nb: 'Det utfylte treet du valgte å laste ned inneholder poster i ranger som mangler i treet ditt.',
  },
  createMissingRanks: {
    'en-us': 'Should the missing ranks be created?',
    'de-ch': 'Sollen die fehlenden Dienstgrade geschaffen werden?',
    'es-es': '¿Deberían crearse los rangos que faltan?',
    'fr-fr': 'Faut-il créer les grades manquants ?',
    'hr-hr': 'Treba li stvoriti nedostajuće redove?',
    'pt-br': 'Deveriam ser criadas as patentes que faltam?',
    'ru-ru': 'Следует ли создать недостающие звания?',
    'uk-ua': 'Чи слід створювати відсутні ряди?',
    nb: 'Bør de manglende rekkene opprettes?',
  },
  source: {
    'en-us': 'Source',
    'de-ch': 'Quelle',
    'es-es': 'Fuente',
    'fr-fr': 'Source',
    'hr-hr': 'Izvor',
    'pt-br': 'Fonte',
    'ru-ru': 'Источник',
    'uk-ua': 'Джерело',
    nb: 'Kilde',
  },
  initializeEmptyTree: {
    'en-us': 'Initialize an empty tree',
    'de-ch': 'Initialisiere einen leeren Baum',
    'es-es': 'Inicializar un árbol vacío',
    'fr-fr': 'Initialiser un arbre vide',
    'hr-hr': 'Inicijaliziraj prazno stablo',
    'pt-br': 'Inicialize uma árvore vazia',
    'ru-ru': 'Инициализировать пустое дерево',
    'uk-ua': 'Ініціалізувати порожнє дерево',
    nb: 'Initialiser et tomt tre',
  },
  treeManagement: {
    'en-us': 'Tree Management',
    'de-ch': 'Baumpflege',
    'es-es': 'Gestión de árboles',
    'fr-fr': 'Gestion des arbres',
    'pt-br': 'Gestão de árvores',
    'ru-ru': 'Управление деревьями',
    'uk-ua': 'Управління деревами',
    'hr-hr': 'Upravljanje drvećem',
    nb: 'Treforvaltning',
  },
  synonymizedNodes: {
    'en-us': 'Expand Synonym Behavior',
    'de-ch': 'Synonymverhalten erweitern',
    'es-es': 'Expandir el comportamiento de los sinónimos',
    'fr-fr': 'Étendre le comportement des synonymes',
    'pt-br': 'Expandir o comportamento dos sinônimos',
    'ru-ru': 'Развернуть поведение синонимов',
    'uk-ua': 'Розгорнути поведінку синонімів',
    'hr-hr': 'Proširi ponašanje sinonima',
    nb: 'Utvid synonymoppførsel',
  },
  synonymizedNodesDescription: {
    'en-us':
      'If enabled, this allows users to add children to synonymized parents and to synonymize a node with children.',
    'de-ch':
      'Ist diese Funktion aktiviert, können Benutzer Kindknoten zu synonymisierten Elternknoten hinzufügen und einen Knoten mit Kindknoten synonymisieren.',
    'es-es':
      'Si está habilitada, esta opción permite a los usuarios agregar nodos hijos a nodos padres sinonimizados y sinonimizar un nodo con sus nodos hijos.',
    'fr-fr':
      "Si cette option est activée, elle permet aux utilisateurs d'ajouter des enfants à des parents synonymisés et de synonymiser un nœud avec ses enfants.",
    'pt-br':
      'Se ativada, essa opção permite que os usuários adicionem filhos a nós pais sinonimizados e sinonimizem um nó com seus filhos.',
    'ru-ru':
      'Если эта функция включена, она позволяет пользователям добавлять дочерние элементы к синонимизированным родительским элементам и синонимизировать узел с дочерними элементами.',
    'uk-ua':
      'Якщо це ввімкнено, користувачі можуть додавати дочірні елементи до синонімізованих батьківських елементів та синонімізувати вузол з дочірніми елементами.',
    'hr-hr':
      'Ako je omogućeno, ovo korisnicima omogućuje dodavanje djece sinonimiziranim roditeljima i sinonimizaciju čvora s djecom.',
    nb: 'Hvis dette er aktivert, kan brukere legge til barn til synonymiserte foreldre og synonymisere en node med barn.',
  },
  utf8EncodingWarning: {
    'en-us':
      'This tree data cannot be imported in your database because it contains UTF-8 characters and this database uses {encoding:string}',
    'de-ch':
      'Diese Baumdaten können nicht in Ihre Datenbank importiert werden, da sie UTF-8-Zeichen enthalten und diese Datenbank {encoding:string} verwendet.',
    'es-es':
      'Estos datos de árbol no se pueden importar en su base de datos porque contienen caracteres UTF-8 y esta base de datos utiliza {encoding:string}.',
    'fr-fr':
      "Ces données d'arbre ne peuvent pas être importées dans votre base de données car elles contiennent des caractères UTF-8 et cette base de données utilise {encoding:string}.",
    'hr-hr':
      'Podaci o ovom stablu ne mogu se uvesti u vašu bazu podataka jer sadrže UTF-8 znakove, a ova baza podataka koristi {encoding:string}',
    nb: 'Disse tredataene kan ikke importeres til databasen din fordi de inneholder UTF-8-tegn, og denne databasen bruker {encoding:string}',
    'pt-br':
      'Esses dados da árvore não podem ser importados para o seu banco de dados porque contêm caracteres UTF-8 e este banco de dados usa {encoding:string}',
    'ru-ru':
      'Данные о дереве невозможно импортировать в вашу базу данных, поскольку они содержат символы UTF-8, а в этой базе данных используется кодировка {encoding:string}.',
    'uk-ua':
      'Дані цього дерева неможливо імпортувати до вашої бази даних, оскільки вони містять символи UTF-8, а ця база даних використовує {encoding:string}',
  },
  resolveEncoding: {
    'en-us': 'How to resolve:',
    'de-ch': 'So lässt sich das Problem lösen:',
    'es-es': 'Cómo resolverlo:',
    'fr-fr': 'Comment résoudre ce problème :',
    'hr-hr': 'Kako riješiti:',
    nb: 'Slik løser du dette:',
    'pt-br': 'Como resolver:',
    'ru-ru': 'Как решить проблему:',
    'uk-ua': 'Як вирішити:',
  },
} as const);
